import React, { useState } from "react";
import { Arrow as LaagArrow, useLayer } from "react-laag";
import type { ArrowProps, Placement } from "react-laag";

// react-laag 2.x declares `Arrow` with its prop keys Pick-ed from the React 17
// SVG typings, which no longer line up with @types/react 19.
const Arrow = LaagArrow as unknown as React.ForwardRefExoticComponent<
  ArrowProps & React.RefAttributes<SVGSVGElement>
>;

interface PopoverMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  menu?: (close: () => void) => React.ReactNode;
  placement?: Placement;
  /** While true the trigger ignores clicks and keyboard activation. */
  disabled?: boolean;
}

const PopoverMenu = ({
  children,
  menu,
  placement,
  disabled = false,
  ...other
}: PopoverMenuProps) => {
  const [isOpen, propsHover] = useState(false);

  // `close` used to be a toggle wired to onOutsideClick, onDisappear *and* the
  // trigger's onClick — clicking the trigger to dismiss fired it twice and the
  // menu stayed open. Closing and toggling are now distinct.
  const close = () => propsHover(false);
  const toggle = () => propsHover((old) => !old);
  const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
    isOpen,
    placement: placement || "top-start",
    auto: !placement,
    arrowOffset: 4,
    triggerOffset: 10,
    onOutsideClick: close, // close the menu when the user clicks outside
    onDisappear: close, // close the menu when the menu gets scrolled out of sight
  });

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    } else if (e.key === "Escape" && isOpen) {
      close();
    }
  };

  return (
    <>
      <div
        {...triggerProps}
        {...other}
        role={"button"}
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup={"menu"}
        aria-disabled={disabled || undefined}
        onKeyDown={onKeyDown}
        onClick={disabled ? undefined : toggle}
      >
        {children}
      </div>
      {isOpen &&
        renderLayer(
          <div
            {...layerProps}
            style={{ ...layerProps.style }}
            className={"popover-menu"}
          >
            {menu && menu(close)}
            <Arrow {...arrowProps} size={5} roundness={0} />
          </div>,
        )}
    </>
  );
};

export default PopoverMenu;
